import { Icon } from '@aibees/shared/components';
import { SHARED_ROUTES } from '@aibees/shared/constants';
import { useBoolean } from '@aibees/shared/hooks';
import { HEADER } from '@aibees/shared/layouts/config-layout';
import { classnames } from '@aibees/shared/utils';
import { Divider } from '@mui/material';
import { memo } from 'react';
import { SidebarItemsExpanded } from './components/sidebar-items-expanded';
import { SidebarItemsMini } from './components/sidebar-items-mini';
import { SidebarSingleItem } from './components/sidebar-single-item';
import * as Styled from './styles';
import * as SX from './sx';
import type { SidebarProps as Props } from './types';

const PAPER_PROPS = { sx: SX.paper };

const highPriorityImgProps = {
  fetchPriority: 'high',
} as React.ImgHTMLAttributes<HTMLImageElement>;

export const Sidebar = memo(({ className, options, withOutZIndex }: Props) => {
  const state = useBoolean();

  return (
    <Styled.Wrapper
      anchor="left"
      PaperProps={PAPER_PROPS}
      open={state.value}
      variant="permanent"
      className={classnames('Sidebar', className)}
      sx={SX.root(withOutZIndex)}
    >
      <Styled.MenuContainer
        className={classnames('scroll-hidden', {
          open: state.value,
          close: !state.value,
        })}
      >
        <Styled.LogoWrapper
          sx={{
            minHeight: {
              lg: HEADER.TOP,
              md: HEADER.T_TABLET,
              xs: HEADER.T_MOBILE,
            },
          }}
          className={classnames({ open: state.value })}
        >
          {!state.value ? (
            <Styled.Logo
              src="/assets/images/ark-logo.svg"
              alt="logo"
              {...highPriorityImgProps}
            />
          ) : (
            <Styled.Brand
              src="/assets/images/ai-ark.svg"
              alt="logo"
              {...highPriorityImgProps}
            />
          )}
          {state.value && (
            <Icon
              name="sidebar"
              onClick={state.onFalse}
              size={18}
              sxx={SX.icon}
            />
          )}
        </Styled.LogoWrapper>
        <SidebarSingleItem
          title="Home"
          icon="home"
          open={state.value}
          route={SHARED_ROUTES.home}
          homePath={options?.homeRoute}
        />
        <Divider variant="fullWidth" flexItem orientation="horizontal" />
        {options?.singleItems?.slice(0, 2).map((singleItem) => (
          <SidebarSingleItem
            actionMode={singleItem.actionMode}
            key={singleItem.route}
            title={singleItem.title}
            icon={singleItem.icon}
            open={state.value}
            route={singleItem.route}
            comingSoon={singleItem.comingSoon}
          />
        ))}
        <Divider variant="fullWidth" flexItem orientation="horizontal" />
        {options?.singleItems?.slice(2, 6).map((singleItem) => (
          <SidebarSingleItem
            actionMode={singleItem.actionMode}
            key={singleItem.route}
            title={singleItem.title}
            icon={singleItem.icon}
            open={state.value}
            route={singleItem.route}
            comingSoon={singleItem.comingSoon}
          />
        ))}
        <Divider variant="fullWidth" flexItem orientation="horizontal" />
        {options?.singleItems?.slice(6).map((singleItem) => (
          <SidebarSingleItem
            actionMode={singleItem.actionMode}
            key={singleItem.route}
            title={singleItem.title}
            icon={singleItem.icon}
            open={state.value}
            route={singleItem.route}
            comingSoon={singleItem.comingSoon}
          />
        ))}
        {state.value ? (
          <SidebarItemsExpanded options={options} state={state} />
        ) : (
          <SidebarItemsMini options={options} onOpenSidbar={state.onTrue} />
        )}
        <SidebarSingleItem
          title="Settings"
          icon="setting-1"
          route={SHARED_ROUTES.settings}
          open={state.value}
          sx={SX.settings}
          hideBadgeName
        />
      </Styled.MenuContainer>
      <Styled.Backdrop
        className={classnames({ show: state.value })}
        onClick={state.onFalse}
      />
    </Styled.Wrapper>
  );
});

Sidebar.displayName = 'Sidebar';
